import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../../../api/axios';

const PREFETCH_TTL_MS = 30000;

export function useQuestionsPageData({
  selectedCourse,
  notebookId,
  category,
  viewWeak,
  isTeacher,
}) {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notebooks, setNotebooks] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [sortBy, setSortBy] = useState('questionNumber');
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const prefetchedRef = useRef({});

  useEffect(() => {
    if (category) {
      setSelectedCategory(category);
    } else if (notebookId) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory('all');
    }
  }, [category, notebookId]);

  const getCacheKey = useCallback(() => {
    if (notebookId) return `notebook_${notebookId}`;
    if (selectedCategory && selectedCategory !== 'all') return `category_${selectedCategory}`;
    return 'all';
  }, [notebookId, selectedCategory]);

  const fetchData = useCallback(async (signal) => {
    if (!selectedCourse?.id) {
      setQuestions([]);
      setCategories([]);
      return;
    }
    const courseTemplateId = selectedCourse.id;
    const cacheKey = getCacheKey();
    let questionsParams = {
      courseTemplateId: parseInt(courseTemplateId, 10),
    };
    if (notebookId) {
      questionsParams = { courseTemplateId: parseInt(courseTemplateId, 10), notebookId };
    } else if (selectedCategory && selectedCategory !== 'all') {
      questionsParams.category = selectedCategory;
    }
    if (viewWeak) {
      questionsParams.view = 'weak';
    }
    const needCategories = categories.length === 0;
    const cached = prefetchedRef.current[cacheKey];
    const useCacheFirst = cached && Date.now() - cached.timestamp < PREFETCH_TTL_MS;

    if (useCacheFirst) {
      let filtered = cached.questions || [];
      if (notebookId) {
        filtered = filtered.filter((q) => q.isNotebookExample && q.notebookId === notebookId);
      }
      setQuestions(filtered);
      setLoading(false);
      return;
    }
    setLoading(true);
    const reqConfig = { signal };
    try {
      if (needCategories) {
        const [questionsRes, categoriesRes] = await Promise.all([
          axios.get('/questions', { params: questionsParams, ...reqConfig }),
          axios.get('/questions/categories', {
            params: { courseTemplateId: parseInt(courseTemplateId, 10) },
            ...reqConfig,
          }),
        ]);
        let filteredQuestions = questionsRes.data || [];
        if (notebookId) {
          filteredQuestions = filteredQuestions.filter(
            (q) => q.isNotebookExample && q.notebookId === notebookId
          );
        }
        prefetchedRef.current[cacheKey] = { questions: questionsRes.data || [], timestamp: Date.now() };
        setQuestions(filteredQuestions);
        setCategories(categoriesRes.data || []);
      } else {
        const questionsRes = await axios.get('/questions', { params: questionsParams, ...reqConfig });
        let filteredQuestions = questionsRes.data || [];
        if (notebookId) {
          filteredQuestions = filteredQuestions.filter(
            (q) => q.isNotebookExample && q.notebookId === notebookId
          );
        }
        prefetchedRef.current[cacheKey] = { questions: questionsRes.data || [], timestamp: Date.now() };
        setQuestions(filteredQuestions);
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching data:', error);
      setQuestions([]);
      if (needCategories) setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, notebookId, selectedCategory, viewWeak, getCacheKey, categories.length]);

  const fetchNotebooks = useCallback(async (signal) => {
    try {
      if (!selectedCourse?.code) {
        setNotebooks([]);
        return;
      }
      const response = await axios.get(
        `/notes/course/${selectedCourse.code.toUpperCase()}`,
        { signal }
      );
      setNotebooks(response.data || []);
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching notebooks:', error);
      setNotebooks([]);
    }
  }, [selectedCourse?.code]);

  const prefetchQuestions = useCallback(
    (targetNotebookId, targetCategory) => {
      if (!selectedCourse?.id) return;
      const key = targetNotebookId ? `notebook_${targetNotebookId}` : `category_${targetCategory}`;
      if (
        prefetchedRef.current[key] &&
        Date.now() - prefetchedRef.current[key].timestamp < PREFETCH_TTL_MS
      ) {
        return;
      }
      const params = { courseTemplateId: parseInt(selectedCourse.id, 10) };
      if (targetNotebookId) params.notebookId = targetNotebookId;
      else if (targetCategory) params.category = targetCategory;
      axios
        .get('/questions', { params })
        .then((res) => {
          prefetchedRef.current[key] = { questions: res.data || [], timestamp: Date.now() };
        })
        .catch(() => {});
    },
    [selectedCourse?.id]
  );

  // 进入笔记本页时立即设为 loading，避免用上一页的「全部题目」渲染整张大列表导致卡顿
  useEffect(() => {
    if (!selectedCourse?.id) return;
    if (notebookId) {
      setLoading(true);
    }
    const ctrl = new AbortController();
    fetchData(ctrl.signal);
    if (isTeacher) fetchNotebooks(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedCourse, notebookId, viewWeak, isTeacher]);

  const refetchData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    questions,
    setQuestions,
    categories,
    loading,
    notebooks,
    expandedSections,
    setExpandedSections,
    sortBy,
    setSortBy,
    selectedCategory,
    setSelectedCategory,
    prefetchQuestions,
    refetchData,
  };
}
